"use client";
import {
	type EmbeddedDashboard,
	embedDashboard,
} from "@superset-ui/embedded-sdk";
import { useEffect, useRef, useState } from "react";
import { fetchSupersetGuestToken } from "../../../api/supersetApi";

export function EmbedPanel({ type = "market" }: { type?: "market" | "user" }) {
	const mountRef = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let embedded: EmbeddedDashboard | null = null;
		let cancelled = false;

		async function mount() {
			if (!mountRef.current) return;
			try {
				const meta = await fetchSupersetGuestToken(type);
				if (cancelled || !mountRef.current) return;

				embedded = await embedDashboard({
					id: meta.dashboardUuid,
					supersetDomain: meta.domain,
					mountPoint: mountRef.current,
					fetchGuestToken: async () =>
						(await fetchSupersetGuestToken(type)).token,
					dashboardUiConfig: {
						hideTitle: true,
						filters: { visible: true, expanded: false },
					},
				});
				if (cancelled) {
					embedded.unmount();
					return;
				}
				embedded.setThemeMode("dark");
				setLoading(false);
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: "Failed to load embedded dashboard",
					);
					setLoading(false);
				}
			}
		}

		void mount();
		return () => {
			cancelled = true;
			embedded?.unmount();
		};
	}, [type]);

	return (
		<div className="card bg-base-300 shadow-xl">
			<div className="card-body p-2 md:p-3">
				{error && (
					<div className="text-xs font-mono text-error py-8 text-center px-4">
						Couldn't load the embedded Superset dashboard: {error}
					</div>
				)}
				{loading && !error && (
					<div className="flex items-center justify-center gap-2 text-xs font-mono text-base-content/50 py-10">
						<span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
						<span>Loading embedded dashboard…</span>
					</div>
				)}
				<div
					ref={mountRef}
					className="w-full [&>iframe]:w-full [&>iframe]:min-h-[1400px] [&>iframe]:border-0 [&>iframe]:rounded"
				/>
			</div>
		</div>
	);
}
